import { useEffect, useMemo, useRef, useState } from 'react';
import type { BulkPreviewItem as InventoryBulkPreviewRow } from '../domain/catalogTypes';
import { categoryAppliesToShortLabel, type CategoryOption } from '../domain/categoryOptions';
import { isBulkPreviewScopeAllowed } from '../application/bulkPreviewScope';
import { matchesSearchText } from '../../shared/format/searchText';

export type BulkApplyTo = 'prices' | 'quantities';

/** Tope de filas traídas / aplicables por corrida (API local). */
export const BULK_COEFFICIENT_PREVIEW_FETCH_LIMIT = 500;

/** @deprecated Usar FlatList con todas las filas cargadas; se mantiene por compat. */
export const BULK_COEFFICIENT_PREVIEW_DISPLAY_LIMIT = BULK_COEFFICIENT_PREVIEW_FETCH_LIMIT;

export type UseBulkCoefficientModalParams = {
  visible: boolean;
  bulkCategory: string | 'all';
  bulkSearch: string;
  bulkPercentInput: string;
  bulkApplyTo: BulkApplyTo;
  bulkPreview: InventoryBulkPreviewRow[];
  categoryOptions: CategoryOption[];
  onCategoryChange: (value: string | 'all') => void;
  onClearPreview?: () => void;
};

export const useBulkCoefficientModal = ({
  visible,
  bulkCategory,
  bulkSearch,
  bulkPercentInput,
  bulkApplyTo,
  bulkPreview,
  categoryOptions,
  onCategoryChange,
  onClearPreview,
}: UseBulkCoefficientModalParams) => {
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');

  const selectedCategory = categoryOptions.find((opt) => opt.id === bulkCategory);
  const selectedCategoryScope = selectedCategory?.appliesTo ? categoryAppliesToShortLabel(selectedCategory.appliesTo) : null;
  const selectedCategoryLabel = bulkCategory === 'all' ? 'Todos los productos' : selectedCategory?.label ?? 'Categoría';
  const selectedCategoryHint =
    bulkCategory === 'all'
      ? `${categoryOptions.length} categorías disponibles`
      : selectedCategoryScope ?? 'Categoría seleccionada';

  const visibleCategoryOptions = useMemo(() => {
    const q = categorySearch.trim().toLowerCase();
    if (!q) return categoryOptions;
    return categoryOptions.filter((opt) => {
      const scope = opt.appliesTo ? categoryAppliesToShortLabel(opt.appliesTo) : '';
      return matchesSearchText(`${opt.label} ${scope}`, categorySearch);
    });
  }, [categoryOptions, categorySearch]);

  const selectCategory = (value: string | 'all') => {
    onCategoryChange(value);
    setCategoryPickerOpen(false);
    setCategorySearch('');
  };

  const previewRows = useMemo(
    () => [...bulkPreview].sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })),
    [bulkPreview]
  );

  const scopeReady = isBulkPreviewScopeAllowed(bulkCategory, bulkSearch);

  useEffect(() => {
    if (!visible) {
      setCategoryPickerOpen(false);
      setCategorySearch('');
    }
  }, [visible]);

  const skipClearPreviewOnMountRef = useRef(true);

  useEffect(() => {
    if (skipClearPreviewOnMountRef.current) {
      skipClearPreviewOnMountRef.current = false;
      return;
    }
    if (bulkPreview.length === 0) return;
    onClearPreview?.();
    // onClearPreview es inline en el padre; no incluirlo en deps para evitar loop de setState.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkCategory, bulkSearch, bulkPercentInput, bulkApplyTo]);

  return {
    categoryPickerOpen,
    setCategoryPickerOpen,
    categorySearch,
    setCategorySearch,
    selectedCategoryLabel,
    selectedCategoryHint,
    visibleCategoryOptions,
    selectCategory,
    previewRows,
    scopeReady,
    hasPreview: bulkPreview.length > 0,
    previewCount: bulkPreview.length,
  };
};
